import ExcelJS from 'exceljs';
import type { ColumnMapping, TableFilter } from '../../api/types';
import type { TableField } from '../../api/types';
import {
  getDisplayColumnHeader,
  getDisplayFieldLabel,
} from '../../screens/table-data-detail.utils';
import type {
  AdvancedRowData,
  AggregatedRow,
  DrillStep,
  ExcelSection,
  MetricOption,
  Option,
  TrendDisplayUnit,
  TrendGrain,
  TrendPoint,
  TrendUnitCategory,
  TrendWeightUnit,
} from './types';

const DIMENSION_HINTS = [
  { label: 'Importador', tokens: ['importador', 'importer'] },
  { label: 'Exportador', tokens: ['exportador', 'exporter'] },
  { label: 'Partida Arancelaria', tokens: ['partida arancelaria', 'partida', 'hs code'] },
  {
    label: 'Razon Social Declarante',
    tokens: ['razon social declarante', 'declarante'],
  },
] as const;

const METRIC_HINTS = [
  {
    label: 'Valor CIF',
    tokens: ['valor total cif', 'cif total', 'valor cif total', 'valor cif', 'cif'],
    unit: 'USD',
  },
  {
    label: 'Valor FOB',
    tokens: ['valor total fob', 'fob total', 'valor fob total', 'valor fob', 'fob'],
    unit: 'USD',
  },
  { label: 'Peso Neto', tokens: ['peso neto', 'neto'], unit: 'KG' },
  { label: 'Cantidad', tokens: ['cantidad', 'cant'], unit: 'UND' },
] as const;

const DATE_HINTS = ['fecha', 'date', 'periodo', 'period'];

export const MAX_DYNAMIC_ROWS = 12;
export const MAX_DRILL_DEPTH = 6;
export const ANALYSIS_PREVIOUS_PALETTE = [
  '#1f4bd6',
  '#e53935',
  '#f59e0b',
  '#7c3aed',
  '#10b981',
  '#64748b',
] as const;

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const TREND_UNITS_BY_CATEGORY: Record<TrendUnitCategory, TrendDisplayUnit[]> = {
  currency: ['USD'],
  weight: ['KG', 'LB'],
  count: ['UND'],
  value: ['VALUE'],
};

const EXCHANGE_RATE_HINTS = ['tasa de cambio', 'tipo de cambio', 'trm', 'exchange rate'];

const getAccessorKey = (column: ColumnMapping) =>
  column.optionalName || column.internalName || column.name || column._id || '';

const getLabel = (column: ColumnMapping) =>
  column.publicName || column.name || column.internalName || column._id || '';

const isDateLike = (value: unknown): boolean => {
  if (value instanceof Date) return true;
  if (typeof value !== 'string') return false;
  if (!value.trim()) return false;
  return !Number.isNaN(Date.parse(value));
};

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const toSafeString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
};

const normalizeString = (value: string) => normalize(value);

const isIdAccessor = (accessor: string) => /(?:^|_)(id|nit|cod)(?:$|_)/i.test(accessor);

const isIdLikeValue = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const compact = trimmed.replace(/\s|-/g, '');
  if (compact.length < 4) return false;
  const numericOnly = compact.replace(/\D/g, '');
  return numericOnly.length >= 4 && numericOnly.length >= compact.length * 0.7;
};

const getBaseAccessor = (accessor: string) =>
  accessor.replace(/(?:_)?(?:cod|id|nit)$/i, '').replace(/(?:_)?(?:codigo|identificacion)$/i, '');

const pickCompanionAccessors = (
  records: Array<Record<string, unknown>>,
  dimensionAccessor: string
) => {
  const sample = records[0];
  if (!sample) return { nameAccessor: '', idAccessor: '' };

  const keys = Object.keys(sample).filter(key => key !== dimensionAccessor);
  const dimensionBase = normalize(getBaseAccessor(dimensionAccessor));

  const baseMatched = keys.filter(key => {
    const norm = normalize(key);
    return dimensionBase && (norm.includes(dimensionBase) || dimensionBase.includes(norm));
  });

  const nameCandidates = [...baseMatched, ...keys]
    .filter(key => !isIdAccessor(key))
    .filter(key => {
      const value = sample[key];
      return typeof value === 'string' || value === null || value === undefined;
    })
    .sort((a, b) => {
      const aNorm = normalize(a);
      const bNorm = normalize(b);
      const aScore =
        Number(aNorm.includes('nombre')) +
        Number(aNorm.includes('name')) +
        Number(aNorm.includes('razon')) +
        Number(aNorm.includes('desc'));
      const bScore =
        Number(bNorm.includes('nombre')) +
        Number(bNorm.includes('name')) +
        Number(bNorm.includes('razon')) +
        Number(bNorm.includes('desc'));
      return bScore - aScore;
    });

  const idCandidates = [...baseMatched, ...keys]
    .filter(key => isIdAccessor(key))
    .filter(key => {
      const value = sample[key];
      return typeof value === 'string' || typeof value === 'number';
    });

  return {
    nameAccessor: nameCandidates[0] ?? '',
    idAccessor: idCandidates[0] ?? '',
  };
};

const getMostFrequent = (counter: Map<string, number>) => {
  let winner = '';
  let max = 0;
  counter.forEach((count, value) => {
    if (count > max) {
      max = count;
      winner = value;
    }
  });
  return winner;
};

const uniqueByKey = <T extends { key: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
};

const tokenize = (value: string): string[] =>
  normalize(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const getOverlapScore = (left: string[], right: string[]) => {
  if (!left.length || !right.length) return 0;
  const rightSet = new Set(right);
  return left.reduce((score, token) => score + Number(rightSet.has(token)), 0);
};

const hasUsefulDimensionValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (value instanceof Date) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return Boolean(trimmed) && !isDateLike(trimmed);
  }
  return typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint';
};

const getAccessorCoverage = (
  records: Array<Record<string, unknown>>,
  accessor: string,
  maxRows = 25
): number => {
  if (!accessor) return 0;
  return records
    .slice(0, maxRows)
    .reduce((count, row) => count + Number(hasUsefulDimensionValue(row[accessor])), 0);
};

const resolveFieldDimensionAccessor = (
  field: TableField,
  mapping: ColumnMapping[] | undefined,
  fields: TableField[] | undefined,
  records: Array<Record<string, unknown>>
): string => {
  const sample = records[0];
  const fieldAccessor = field.internalName || field.name || field._id || '';
  if (!fieldAccessor) return '';

  const fieldLabel = getDisplayFieldLabel(field, fields, sample);
  const normalizedFieldLabel = normalize(fieldLabel);
  const normalizedFieldAccessor = normalize(fieldAccessor);
  const normalizedFieldBase = normalize(getBaseAccessor(fieldAccessor));
  const fieldTokens = tokenize(`${fieldLabel} ${fieldAccessor}`);

  const mappingCandidates = (mapping ?? []).map(column => {
    const key = getAccessorKey(column);
    return {
      key,
      label: getDisplayColumnHeader(column, mapping, sample),
    };
  });

  const sampleCandidates = sample
    ? Object.keys(sample).map(key => ({
        key,
        label: key,
      }))
    : [];

  const candidates = uniqueByKey(
    [
      {
        key: fieldAccessor,
        label: fieldLabel,
      },
      ...mappingCandidates,
      ...sampleCandidates,
    ].filter(item => item.key)
  );

  let bestKey = fieldAccessor;
  let bestScore = Number.NEGATIVE_INFINITY;

  candidates.forEach(candidate => {
    const coverage = getAccessorCoverage(records, candidate.key);
    const normalizedCandidateKey = normalize(candidate.key);
    const normalizedCandidateLabel = normalize(candidate.label);
    const normalizedCandidateBase = normalize(getBaseAccessor(candidate.key));
    const candidateTokens = tokenize(`${candidate.label} ${candidate.key}`);

    let score = coverage * 100;

    if (coverage > 0 && candidate.key === fieldAccessor) score += 60;
    if (normalizedCandidateLabel && normalizedCandidateLabel === normalizedFieldLabel) score += 30;
    if (normalizedCandidateKey === normalizedFieldAccessor) score += 20;
    if (normalizedCandidateBase && normalizedCandidateBase === normalizedFieldBase) score += 18;
    if (
      normalizedCandidateBase &&
      normalizedFieldBase &&
      normalizedCandidateBase !== normalizedFieldBase &&
      (normalizedCandidateBase.includes(normalizedFieldBase) ||
        normalizedFieldBase.includes(normalizedCandidateBase))
    ) {
      score += 10;
    }

    score += getOverlapScore(candidateTokens, fieldTokens) * 4;

    if (score > bestScore) {
      bestScore = score;
      bestKey = candidate.key;
    }
  });

  return bestKey;
};

const pickColumnByTokens = (mapping: ColumnMapping[] | undefined, tokens: string[]) => {
  if (!mapping?.length) return undefined;
  const normalizedTokens = tokens.map(normalize);

  let bestMatch: ColumnMapping | undefined;
  let bestScore = Number.NEGATIVE_INFINITY;

  mapping.forEach(column => {
    const label = normalize(getLabel(column));
    const matchedTokens = normalizedTokens.filter(token => label.includes(token));
    if (!matchedTokens.length) return;

    const longestMatch = Math.max(...matchedTokens.map(token => token.length));
    const totalBonus = label.includes('total') ? 4 : 0;
    const unitPenalty = label.includes('unitario') || label.includes('unit ') ? -6 : 0;
    const score = longestMatch + totalBonus + unitPenalty;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = column;
    }
  });

  return bestMatch;
};

export const formatMetric = (value: number) =>
  value.toLocaleString('es-CO', {
    maximumFractionDigits: 2,
  });

export const getTrendUnitCategory = (metricLabel: string): TrendUnitCategory => {
  const normalized = normalize(metricLabel);
  if (!normalized) return 'value';
  if (normalized.includes('valor cif') || normalized.includes('valor fob')) {
    return 'currency';
  }
  if (normalized.includes('peso neto') || normalized.includes('neto')) {
    return 'weight';
  }
  if (normalized.includes('cantidad') || normalized.includes('cant')) {
    return 'count';
  }
  return 'value';
};

export const getUnitOptionsByCategory = (category: TrendUnitCategory): TrendDisplayUnit[] =>
  TREND_UNITS_BY_CATEGORY[category];

export const getCurrencyUnitOptions = (hasExchangeRateColumn: boolean): TrendDisplayUnit[] => {
  return hasExchangeRateColumn ? ['USD', 'COP'] : ['USD'];
};

export const convertWeight = (
  amount: number,
  from: TrendWeightUnit = 'KG',
  to: TrendWeightUnit = 'LB'
): number => {
  if (from === to) return amount;
  const KG_TO_LB = 2.2046226218;
  if (from === 'KG' && to === 'LB') return amount * KG_TO_LB;
  if (from === 'LB' && to === 'KG') return amount / KG_TO_LB;
  return amount;
};

export const detectDateAccessor = (
  mapping: ColumnMapping[] | undefined,
  records: Array<Record<string, unknown>>
) => {
  if (mapping?.length) {
    const hinted = mapping.find(column => {
      const label = normalize(getLabel(column));
      return DATE_HINTS.some(token => label.includes(token));
    });
    const hintedAccessor = hinted ? getAccessorKey(hinted) : '';
    if (hintedAccessor) return hintedAccessor;
  }

  const sample = records[0];
  if (!sample) return '';
  const key = Object.keys(sample).find(candidate => isDateLike(sample[candidate]));
  return key ?? '';
};

export const detectExchangeRateAccessor = (
  mapping: ColumnMapping[] | undefined,
  records: Array<Record<string, unknown>>
) => {
  if (mapping?.length) {
    const hinted = mapping.find(column => {
      const label = normalize(getLabel(column));
      return EXCHANGE_RATE_HINTS.some(token => label.includes(token));
    });
    const hintedAccessor = hinted ? getAccessorKey(hinted) : '';
    if (hintedAccessor) return hintedAccessor;
  }

  const sample = records[0];
  if (!sample) return '';
  const key = Object.keys(sample).find(candidate => {
    const normalized = normalize(candidate);
    return EXCHANGE_RATE_HINTS.some(token => normalized.includes(token));
  });
  return key ?? '';
};

const COP_METRIC_PREFIX = '__cop_metric__';

export const addCopMetricColumn = (
  records: Array<Record<string, unknown>>,
  metricKey: string,
  exchangeRateAccessor: string
) => {
  if (!metricKey || !exchangeRateAccessor) {
    return { records, metricKey, converted: false };
  }

  const copMetricKey = `${COP_METRIC_PREFIX}${metricKey}`;
  const convertedRecords = records.map(record => {
    const metric = toNumber(record[metricKey]);
    const exchangeRate = toNumber(record[exchangeRateAccessor]);
    const safeRate = Number.isFinite(exchangeRate) && exchangeRate > 0 ? exchangeRate : 1;
    return {
      ...record,
      [copMetricKey]: metric * safeRate,
    };
  });

  return { records: convertedRecords, metricKey: copMetricKey, converted: true };
};

export const buildMetricOptions = (
  mapping: ColumnMapping[] | undefined,
  records: Array<Record<string, unknown>>
): MetricOption[] => {
  const fromHints = METRIC_HINTS.map(item => {
    const column = pickColumnByTokens(mapping, item.tokens as unknown as string[]);
    if (!column) return null;
    const accessor = getAccessorKey(column);
    if (!accessor) return null;
    return {
      key: accessor,
      label: item.label,
      unitDefault: item.unit,
    } as MetricOption;
  }).filter(Boolean) as MetricOption[];

  if (fromHints.length) return uniqueByKey(fromHints);

  const sample = records[0];
  if (!sample) return [];

  const fallback = Object.keys(sample)
    .filter(key => typeof sample[key] === 'number' || !Number.isNaN(Number(sample[key])))
    .slice(0, 6)
    .map(key => ({
      key,
      label: key,
      unitDefault: 'VALUE',
    }));

  return uniqueByKey(fallback);
};

export const buildDimensionOptions = (
  mapping: ColumnMapping[] | undefined,
  records: Array<Record<string, unknown>>,
  fields?: TableField[],
  filters?: TableFilter[]
): Option[] => {
  const allowedAnalysisCategories = new Set([
    'partida',
    'importer',
    'exporter',
    'supplier',
    'client',
    'proveedor',
  ]);

  const allowedFieldOptions = (fields ?? [])
    .filter(field => field.visibility !== 'hidden')
    .filter(field => {
      const category = typeof field.category === 'string' ? field.category.toLowerCase() : '';
      const pilar = typeof field.pilar === 'string' ? field.pilar.toLowerCase() : '';
      return pilar === 'main' || pilar === 'aux' || allowedAnalysisCategories.has(category);
    })
    .map(field => {
      const key = resolveFieldDimensionAccessor(field, mapping, fields, records);
      const label = getDisplayFieldLabel(field, fields, records[0]);
      return {
        key: key ?? '',
        label: label ?? '',
      };
    })
    .filter(item => item.key && item.label);

  const determinantFieldIds = new Set(
    (filters ?? []).filter(f => f.isDeterminant === true).map(f => f.field)
  );

  const determinantFieldOptions = (fields ?? [])
    .filter(field => determinantFieldIds.has(field._id))
    .filter(field => field.visibility !== 'hidden')
    .map(field => ({
      key: resolveFieldDimensionAccessor(field, mapping, fields, records) ?? '',
      label: getDisplayFieldLabel(field, fields, records[0]) ?? '',
    }))
    .filter(item => item.key && item.label);

  const combinedFieldOptions = uniqueByKey([...allowedFieldOptions, ...determinantFieldOptions]);

  if (fields?.length) {
    const sample = records[0];
    const filteredAllowed = combinedFieldOptions.filter(item => {
      if (!sample) return true;
      const value = sample[item.key];
      return !isDateLike(value);
    });
    return uniqueByKey(filteredAllowed).slice(0, 40);
  }

  const fromHints = DIMENSION_HINTS.map(item => {
    const column = pickColumnByTokens(mapping, item.tokens as unknown as string[]);
    if (!column) return null;
    const accessor = getAccessorKey(column);
    if (!accessor) return null;
    return {
      key: accessor,
      label: getDisplayColumnHeader(column, mapping, records[0]) || item.label,
    };
  }).filter(Boolean) as Option[];

  const sample = records[0];
  if (!sample) return uniqueByKey(fromHints);

  const dynamicFromMapping = (mapping ?? [])
    .map(column => ({
      key: getAccessorKey(column),
      label: getDisplayColumnHeader(column, mapping, records[0]),
    }))
    .filter(item => item.key)
    .filter(item => {
      const sampleValue = sample[item.key];
      return typeof sampleValue === 'string' || sampleValue === null || sampleValue === undefined;
    })
    .filter(item => !isDateLike(sample[item.key]));

  const dynamicFromSample = Object.keys(sample)
    .filter(
      key => typeof sample[key] === 'string' || sample[key] === null || sample[key] === undefined
    )
    .filter(key => !isDateLike(sample[key]))
    .map(key => ({ key, label: key }));

  return uniqueByKey([...fromHints, ...dynamicFromMapping, ...dynamicFromSample]).slice(0, 40);
};

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const buildTrendBucket = (date: Date, grain: TrendGrain): { bucketKey: string; label: string } => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (grain === 'day') {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return { bucketKey: key, label: key };
  }

  if (grain === 'month') {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    return { bucketKey: key, label: key };
  }

  if (grain === 'quarter') {
    const quarter = Math.floor((month - 1) / 3) + 1;
    const key = `${year}-Q${quarter}`;
    return { bucketKey: key, label: `Q${quarter} ${year}` };
  }

  return { bucketKey: `${year}`, label: `${year}` };
};

export const aggregateTrend = (
  records: Array<Record<string, unknown>>,
  dateAccessor: string,
  metricKey: string,
  grain: TrendGrain
): TrendPoint[] => {
  if (!dateAccessor || !metricKey) return [];

  const buckets = new Map<string, TrendPoint>();

  records.forEach(row => {
    const date = toDate(row[dateAccessor]);
    if (!date) return;
    const { bucketKey, label } = buildTrendBucket(date, grain);
    const metric = toNumber(row[metricKey]);
    const existing = buckets.get(bucketKey) ?? {
      bucketKey,
      label,
      metricValue: 0,
      count: 0,
      timestamp: date.getTime(),
    };

    existing.metricValue += metric;
    existing.count += 1;
    if (date.getTime() < existing.timestamp) {
      existing.timestamp = date.getTime();
    }

    buckets.set(bucketKey, existing);
  });

  return Array.from(buckets.values()).sort((a, b) => a.timestamp - b.timestamp);
};

export const aggregateByDimension = (
  records: Array<Record<string, unknown>>,
  dimensionAccessor: string,
  metricKey: string,
  topN: number
): AdvancedRowData => {
  if (!dimensionAccessor || !metricKey) {
    return {
      rows: [],
      totalMetric: 0,
      othersMetric: 0,
      updatedAt: new Date().toISOString(),
    };
  }

  const { nameAccessor, idAccessor } = pickCompanionAccessors(records, dimensionAccessor);
  const bucketMap = new Map<
    string,
    {
      label: string;
      metricValue: number;
      count: number;
      nameCounts: Map<string, number>;
      idCounts: Map<string, number>;
    }
  >();
  const dimensionAccessorLooksId = isIdAccessor(dimensionAccessor);

  records.forEach(row => {
    const dimensionRaw = toSafeString(row[dimensionAccessor]).trim();
    const key = dimensionRaw || 'Sin dato';
    const metricValue = toNumber(row[metricKey]);
    const existing = bucketMap.get(key) ?? {
      label: key,
      metricValue: 0,
      count: 0,
      nameCounts: new Map<string, number>(),
      idCounts: new Map<string, number>(),
    };

    existing.metricValue += metricValue;
    existing.count += 1;

    if (nameAccessor) {
      const nameValue = toSafeString(row[nameAccessor]).trim();
      if (nameValue) {
        existing.nameCounts.set(nameValue, (existing.nameCounts.get(nameValue) ?? 0) + 1);
      }
    }

    if (idAccessor) {
      const idValue = toSafeString(row[idAccessor]).trim();
      if (idValue) {
        existing.idCounts.set(idValue, (existing.idCounts.get(idValue) ?? 0) + 1);
      }
    }

    bucketMap.set(key, existing);
  });

  const ordered = Array.from(bucketMap.entries())
    .map(([key, value]) => {
      const frequentName = getMostFrequent(value.nameCounts);
      const frequentId = getMostFrequent(value.idCounts);
      const valueLooksId = isIdLikeValue(key);
      const useNameAsPrimary = (dimensionAccessorLooksId || valueLooksId) && Boolean(frequentName);

      const primaryLabel = useNameAsPrimary ? frequentName : value.label;
      const secondaryLabel = useNameAsPrimary
        ? key
        : frequentId && frequentId !== primaryLabel
          ? frequentId
          : undefined;

      return {
        key,
        label: primaryLabel || value.label,
        secondaryLabel,
        metricValue: value.metricValue,
        count: value.count,
        sharePct: 0,
      };
    })
    .sort((a, b) => b.metricValue - a.metricValue);

  const safeTopN = Number.isFinite(topN) && topN > 0 ? Math.floor(topN) : 10;
  const top = ordered.slice(0, safeTopN);
  const others = ordered.slice(safeTopN);

  const othersMetric = others.reduce((sum, row) => sum + row.metricValue, 0);
  const othersCount = others.reduce((sum, row) => sum + row.count, 0);
  const totalMetric = ordered.reduce((sum, row) => sum + row.metricValue, 0);

  const rows: AggregatedRow[] = top.map(row => ({
    ...row,
    sharePct: totalMetric > 0 ? (row.metricValue / totalMetric) * 100 : 0,
  }));

  if (others.length > 0) {
    rows.push({
      key: '__OTHERS__',
      label: 'OTROS',
      secondaryLabel: undefined,
      metricValue: othersMetric,
      count: othersCount,
      sharePct: totalMetric > 0 ? (othersMetric / totalMetric) * 100 : 0,
      isOthers: true,
    });
  }

  return {
    rows,
    totalMetric,
    othersMetric,
    updatedAt: new Date().toISOString(),
  };
};

export const applyDrillPath = (records: Array<Record<string, unknown>>, drillPath: DrillStep[]) => {
  if (!drillPath.length) return records;

  return records.filter(record =>
    drillPath.every(step => {
      const raw = toSafeString(record[step.dimensionAccessor]).trim();
      return normalizeString(raw) === normalizeString(step.valueKey);
    })
  );
};

export const toggleOrSetDrillStep = (drillPath: DrillStep[], nextStep: DrillStep): DrillStep[] => {
  const existingIndex = drillPath.findIndex(
    step => step.dimensionAccessor === nextStep.dimensionAccessor
  );

  if (existingIndex === -1) {
    if (drillPath.length >= MAX_DRILL_DEPTH) return drillPath;
    return [...drillPath, nextStep];
  }

  const current = drillPath[existingIndex];
  if (normalizeString(current.valueKey) === normalizeString(nextStep.valueKey)) {
    return drillPath.slice(0, existingIndex);
  }

  return [...drillPath.slice(0, existingIndex), nextStep];
};

export const isRowSelected = (
  drillPath: DrillStep[],
  rowKey: string,
  dimensionAccessor: string
): boolean => {
  return drillPath.some(
    step =>
      step.dimensionAccessor === dimensionAccessor &&
      normalizeString(step.valueKey) === normalizeString(rowKey)
  );
};

export const buildRowId = () => `row-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

export const DIMENSION_HINT_LABELS = DIMENSION_HINTS.map(item => item.label);

export const escapeHtml = (value: unknown): string => {
  const safeValue = toSafeString(value);
  if (!safeValue) return '';
  return safeValue
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const downloadExcelTable = (rows: Array<Record<string, unknown>>, filename: string) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const headerRow = headers.map(header => `<th>${escapeHtml(header)}</th>`).join('');
  const bodyRows = rows
    .map(row => `<tr>${headers.map(header => `<td>${escapeHtml(row[header])}</td>`).join('')}</tr>`)
    .join('');
  const table = `<table><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>`;

  const blob = new Blob(['\uFEFF', table], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const metricToDownloadRows = (
  rows: AggregatedRow[],
  dimensionLabel: string,
  metricLabel: string
) => {
  return rows.map(row => ({
    [dimensionLabel]: row.label,
    ID: row.secondaryLabel ?? '',
    [metricLabel]: row.metricValue,
    Participacion: `${row.sharePct.toFixed(2)}%`,
    Conteo: row.count,
  }));
};

export type ChartImage = {
  title: string;
  dataUrl: string;
  width: number;
  height: number;
};

export const downloadAdvancedAnalysisExcel = async (
  sections: ExcelSection[],
  filename: string,
  chartImages?: ChartImage[]
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Analisis');

  let currentRow = 1;

  sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      currentRow += 2;
    }

    // Section title
    const titleRow = worksheet.getRow(currentRow);
    titleRow.getCell(1).value = section.title;
    titleRow.getCell(1).font = { bold: true, size: 13 };
    currentRow += 1;

    // Headers
    const headerRow = worksheet.getRow(currentRow);
    section.headers.forEach((header, colIndex) => {
      const cell = headerRow.getCell(colIndex + 1);
      cell.value = header;
      cell.font = { bold: true, size: 10 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' },
      };
    });
    currentRow += 1;

    // Data rows
    section.rows.forEach(row => {
      const dataRow = worksheet.getRow(currentRow);
      row.forEach((cell, colIndex) => {
        dataRow.getCell(colIndex + 1).value = cell;
      });
      currentRow += 1;
    });
  });

  // Auto-fit column widths
  worksheet.columns.forEach(column => {
    let maxLength = 10;
    column.eachCell?.({ includeEmpty: false }, cell => {
      const raw = cell.value;
      const cellLength =
        raw != null && (typeof raw === 'string' || typeof raw === 'number')
          ? String(raw).length
          : 0;
      if (cellLength > maxLength) maxLength = Math.min(cellLength + 2, 50);
    });
    column.width = maxLength;
  });

  // Embed chart images if provided
  if (chartImages?.length) {
    currentRow += 2;
    const chartTitleRow = worksheet.getRow(currentRow);
    chartTitleRow.getCell(1).value = 'Graficos';
    chartTitleRow.getCell(1).font = { bold: true, size: 13 };
    currentRow += 1;

    for (const chart of chartImages) {
      const labelRow = worksheet.getRow(currentRow);
      labelRow.getCell(1).value = chart.title;
      labelRow.getCell(1).font = { bold: true, size: 11 };
      currentRow += 1;

      const imageId = workbook.addImage({
        base64: chart.dataUrl.replace(/^data:image\/png;base64,/, ''),
        extension: 'png',
      });

      const imageHeightRows = Math.max(Math.ceil(chart.height / 20), 12);

      worksheet.addImage(imageId, {
        tl: { col: 0, row: currentRow - 1 } as ExcelJS.Anchor,
        br: { col: 8, row: currentRow - 1 + imageHeightRows } as ExcelJS.Anchor,
      });

      currentRow += imageHeightRows + 1;
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
